import { Component, Input, Output, EventEmitter, Inject, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

@Component({
    selector: 'app-svinn-report-popup',
    templateUrl: './svinn-report-popup.component.html',
    styleUrls: ['./svinn-report-popup.component.css']
})
export class SvinnReportPopupComponent {
    @Input() isOpen: boolean = false;
    @Input() reportData: any;
    @Output() close = new EventEmitter<void>();

    transactions = [];
    svinn = [];
    from: number = 0;
    to: number = 0;
    headerTitle = "Svinn Report";
    excludeWeekends: boolean = false;

    filteredTransactions = [];
    filteredSvinn = [];

    numberOfDays: number = 0;
    dayData: { day: string, date: string, total: number, sales: number, svinn: number }[] = [];
    totals = { total: 0, sales: 0, svinn: 0, salesMedian: 0, svinnMedian: 0, percent: "", salesMax: 0, svinnMax: 0 };


    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
        @Optional() private dialogRef?: MatDialogRef<SvinnReportPopupComponent>
    ) {
        // If this component is opened via MatDialog, the passed data will be available in dialogData.
        // Copy it to reportData so templates can use a single input property regardless of usage method.
        if (dialogData) {
            this.reportData = dialogData;

            this.transactions = dialogData.transactions || [];
            this.svinn = dialogData.svinn || [];
            this.from = dialogData.from || 0;
            this.to = dialogData.to || 0;

            this.excludeWeekends = dialogData.excludeWeekends || false;

            this.headerTitle = dialogData.systemName + " - " + dialogData.category;

            this.numberOfDays = moment.unix(this.to).diff(moment.unix(this.from), 'days');

            this.filteredTransactions = [...this.transactions].reduce((acc, curr) => {
                const entryDate = moment.unix(curr['unix']).tz('Europe/Oslo');
                if (this.excludeWeekends && (entryDate.isoWeekday() === 6 || entryDate.isoWeekday() === 7)) {
                    return acc;
                }
                acc.push(curr);
                return acc;
            }, []);

            this.filteredSvinn = [...this.svinn].reduce((acc, curr) => {
                const entryDate = moment.unix(curr['unix']).tz('Europe/Oslo');
                if (this.excludeWeekends && (entryDate.isoWeekday() === 6 || entryDate.isoWeekday() === 7)) {
                    return acc;
                }
                acc.push(curr);
                return acc;
            }, []);

            this.totals = {
                total: this.filteredTransactions.length + this.filteredSvinn.reduce((sum, s) => sum + (s['number'] || 0), 0),
                sales: this.filteredTransactions.length,
                svinn: this.filteredSvinn.reduce((sum, s) => sum + (s['number'] || 0), 0),
                salesMedian: this.filteredTransactions.length > 0 ? this.calculateMedian(this.filteredTransactions.map(t => t['number'])) : 0,
                svinnMedian: this.filteredSvinn.length > 0 ? this.calculateMedian(this.filteredSvinn.map(s => s['number'])) : 0,
                percent: "",
                salesMax: 0,
                svinnMax: 0
            };

            this.totals.percent = this.totals.total > 0 ? ((this.totals.svinn / this.totals.total) * 100).toFixed(2) : "";

            this.dayData = Array.from({ length: this.numberOfDays + 1 }, (_, i) => {
                const day = moment.unix(this.from).add(i, 'days').startOf('day');

                if (this.excludeWeekends && (day.isoWeekday() === 6 || day.isoWeekday() === 7)) {
                    return null; // Skip weekends
                }
                
                const salesForDay = this.transactions.filter(t => {
                    const tDate = moment.unix(t['unix']).tz('Europe/Oslo');
                    return tDate.isSame(day, 'day');
                }).length;

                const svinnForDay = this.svinn.filter(s => {
                    const sDate = moment.unix(s['unix']).tz('Europe/Oslo');
                    return sDate.isSame(day, 'day');
                }).reduce((sum, s) => sum + (s['number'] || 0), 0);

                return { 
                    date: day.format('DD.MM.YYYY'), 
                    day: day.format('ddd'),
                    total: salesForDay + svinnForDay, 
                    sales: salesForDay, 
                    svinn: svinnForDay
                };
            }).filter(d => d !== null);

            const salesPerDay = this.dayData.map(d => d.sales);
            const svinnPerDay = this.dayData.map(d => d.svinn);

            this.totals.salesMedian = this.calculateMedian(salesPerDay);
            this.totals.svinnMedian = this.calculateMedian(svinnPerDay);

            this.totals.salesMax = Math.max(...salesPerDay);
            this.totals.svinnMax = Math.max(...svinnPerDay);

            this.isOpen = true;
        }
    }

    calculateMedian(data: number[]): number {
        if (data.length === 0) return 0;

        const sorted = [...data].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    }

    closeDialog() {
        // If opened as a dialog, close it through the dialogRef
        if (this.dialogRef) {
            this.dialogRef.close();
        }
        this.close.emit();
    }

    onClose() {
        // If opened as a dialog, close it through the dialogRef
        if (this.dialogRef) {
            this.dialogRef.close();
        }
        this.close.emit();
    }
}